import { schema } from 'normalizr';
import paginateAction from '@utils/paginateAction';
import { itemTypes, plusCoursesTypes } from '../constants';
import { paginatorListSchema, feedRowSchema, itemSchema } from '../schemas';

const getTestSeries = (goalUID, type, resetWithoutEmpty) => (next) => ({
  type: plusCoursesTypes.FETCH_TEST_SERIES,
  endpoint: next,
  method: 'get',
  key: `testSeries_${goalUID}_${type}`,
  resetWithoutEmpty,
  schema: paginatorListSchema,
  directPaginatedEntity: true,
  entityType: 'course'
});

export const fetchTestSeries = (
  goalUID,
  type = 0,
  nextPage,
  resetWithoutEmpty = false,
  forceReset = false
) =>
  paginateAction(
    `v1/uplus/subscription/test_series/?goal_uid=${goalUID}&type=${type}&limit=12`,
    getTestSeries(goalUID, type, resetWithoutEmpty),
    'testSeries',
    `testSeries_${goalUID}_${type}`,
    resetWithoutEmpty,
    forceReset
  )(nextPage);

const getPlusHomeFeed = (goalUid, resetWithoutEmpty) => (next) => ({
  type: plusCoursesTypes.FETCH_PLUS_HOME_FEED,
  endpoint: next,
  method: 'get',
  key: goalUid,
  resetWithoutEmpty,
  schema: paginatorListSchema,
  directPaginatedEntity: true,
  entityType: 'feedrow',
  processData: (data) => {
    const newData = data;
    newData.results = newData.results.map((resultItem) => {
      if (resultItem.type === 'all_topic_groups') {
        const item = {
          ...resultItem,
          data: resultItem.data.map((topicGroup) => ({
            ...topicGroup,
            plusCourseCount: topicGroup.count,
            count: null
          }))
        };
        return { ...item, key: `${goalUid}_plus` };
      }
      return {
        ...resultItem,
        key: `${goalUid}_plus`
      };
    });
    return newData;
  }
});

const getFreeRelatedCourses =
  (lessonUID, resetWithoutEmpty = false) =>
  (next) => ({
    type: itemTypes.FETCH_FREE_RELATED_SPECIAL_ITEMS,
    endpoint: next,
    method: 'get',
    key: lessonUID,
    resetWithoutEmpty,
    schema: paginatorListSchema,
    directPaginatedEntity: true,
    entityType: 'feedrow',
    processData: (data) => {
      const newData = data;
      newData.results = newData.results.map((resultItem) => ({
        ...resultItem,
        key: lessonUID,
        data: resultItem.data.map((dataRow) => ({
          ...dataRow,
          ...dataRow.programme
        }))
      }));
      return newData;
    }
  });

export const fetchFreeRelatedCourses = (
  { lessonUID, authorUID, goalUID, topicUID },
  nextPage,
  resetWithoutEmpty = false
) =>
  paginateAction(
    `v2/post/${lessonUID}/related_special_items?author_uid=${authorUID}&goal_uid=${goalUID}&topic_uid=${topicUID}`,
    getFreeRelatedCourses(lessonUID, resetWithoutEmpty),
    'freeRelatedCourses',
    lessonUID,
    resetWithoutEmpty
  )(nextPage);

export const fetchPlusHomeFeed = (
  goalUid,
  nextPage,
  resetWithoutEmpty = false,
  queryParams = ''
) =>
  paginateAction(
    `v1/uplus/subscription/home/?goal_uid=${goalUid}&v=10${
      queryParams ? `&${queryParams}` : ''
    }`,
    getPlusHomeFeed(goalUid, resetWithoutEmpty),
    'plusHomePageFeed',
    goalUid,
    resetWithoutEmpty
  )(nextPage);

const getSeeAllExtraBlockInfoURL = (seeAllType, topologyUid, blockType) => {
  let url = null;
  if (seeAllType === 'specialClass') {
    url = `v1/uplus/special_class/extra_block_info/?goal_uid=${topologyUid}&block=${blockType}`;
  } else if (seeAllType === 'goal') {
    url = `v1/uplus/subscription/feed_block_extra_info/?topology_uid=${topologyUid}&block_type=${blockType}`;
  } else if (seeAllType === 'freeCourses') {
    url = `v1/feeds/user/extra_block_info/?goal_uid=${topologyUid}&block_type=${blockType}`;
  } else if (seeAllType === 'search') {
    url = `v1/search_v3/extra_block_info/?block_type=${blockType}`;
  }
  return url;
};

export const fetchSeeAllFeedExtraBlockInfo =
  (topologyUid, blockType, seeAllType) => (dispatch) =>
    dispatch({
      type: plusCoursesTypes.FETCH_SEE_ALL_EBINFO,
      endpoint: getSeeAllExtraBlockInfoURL(seeAllType, topologyUid, blockType),
      method: 'get',
      topologyUid,
      blockType,
      seeAllType
    });

export const fetchSimilarPlusCourses = (courseUID) => (dispatch) =>
  dispatch({
    type: plusCoursesTypes.FETCH_SIMILAR,
    endpoint: `v2/collection/${courseUID}/similar_courses/`,
    method: 'get',
    key: courseUID,
    schema: new schema.Object({ feedrow: feedRowSchema }),
    entityType: 'feedrow',
    processData: (data) => {
      const newData = {
        ...data,
        extra_block_info: { ...data.extra_block_info, entity_type: 'Course' }
      };
      newData.data = data.data.map((course) => ({
        ...course,
        ...course.programme
      }));
      newData.key = courseUID;
      return newData;
    }
  });

export const fetchNextTrack = (courseUID) => (dispatch) =>
  dispatch({
    type: plusCoursesTypes.FETCH_NEXT_TRACK,
    endpoint: `v1/collection/${courseUID}/next_track/`,
    method: 'get',
    key: courseUID,
    schema: new schema.Object({
      nextTrack: { item: itemSchema },
      activeQuizSession: { item: itemSchema }
    }),
    processData: (data) => {
      const activeQuizSession = data.active_quiz_session;
      if (activeQuizSession) {
        delete activeQuizSession.collection;
        delete activeQuizSession.programme;
      }
      const newData = {
        nextTrack: data.next_track
          ? { item: { ...data.next_track, type: 'post' } }
          : null,
        activeQuizSession: activeQuizSession
          ? { item: { ...activeQuizSession, type: 'quiz' } }
          : null
      };
      return newData;
    }
  });
